class PawschPetName extends HTMLElement {
  #abortController = new AbortController();
  #input;
  #counter;
  #section;
  #form;
  #variantUpdating = false;
  #variantPoll;
  #variantTimeout;

  connectedCallback() {
    this.#input = this.querySelector('input[name="properties[Pet name]"]');
    this.#counter = this.querySelector('[data-pawsch-character-count]');
    this.#section = this.closest('.shopify-section');
    this.#form = this.#input?.form ?? null;

    if (!this.#input || !this.#section) return;

    const { signal } = this.#abortController;
    this.#section.addEventListener('change', this.#handleVariantChange, { signal });
    this.#section.addEventListener('click', this.#guardAddToCart, { signal, capture: true });
    this.#form?.addEventListener('submit', this.#guardSubmit, { signal, capture: true });
    this.#input.addEventListener('input', this.#handleInput, { signal });
    this.#input.addEventListener('blur', this.#validate, { signal });

    this.#syncVisibility();
    this.#updateCounter();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#stopVariantWait();
  }

  #getPicker() {
    const productId = this.dataset.productId;
    if (!productId || !this.#section) return null;
    return this.#section.querySelector(`variant-picker[data-product-id="${productId}"]`);
  }

  #getPersonalizationValue() {
    const picker = this.#getPicker();
    if (!picker) return null;

    for (const select of picker.querySelectorAll('select')) {
      const selected = select.selectedOptions?.[0];
      if (selected?.dataset?.optionName === 'Personalization') return selected.value;
    }

    const checked = picker.querySelector('fieldset input:checked[data-option-name="Personalization"]');
    return checked?.value ?? null;
  }

  #syncVisibility = () => {
    if (!this.#input) return;

    const shouldShow = this.#getPersonalizationValue() === this.dataset.personalizationValue;
    this.hidden = !shouldShow;
    this.#input.disabled = !shouldShow;
    this.#input.required = shouldShow;

    if (shouldShow) {
      this.#input.setAttribute('aria-required', 'true');
    } else {
      this.#input.removeAttribute('aria-required');
      this.#input.value = '';
      this.#input.setCustomValidity('');
      this.#updateCounter();
    }
  };

  #handleVariantChange = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('variant-picker')) return;

    this.#syncVisibility();
    this.#beginVariantWait(target);
  };

  #beginVariantWait(target) {
    this.#stopVariantWait();
    this.#variantUpdating = true;

    let selected = target;
    if (target instanceof HTMLSelectElement) {
      selected = target.selectedOptions?.[0] ?? target;
    }

    const expectedVariantId = selected instanceof HTMLElement ? selected.dataset.variantId : null;
    const variantInput = this.#form?.querySelector('input[name="id"]');

    if (expectedVariantId && variantInput instanceof HTMLInputElement) {
      this.#variantPoll = window.setInterval(() => {
        if (variantInput.value === expectedVariantId) this.#stopVariantWait();
      }, 25);
    }

    this.#variantTimeout = window.setTimeout(() => this.#stopVariantWait(), 3000);
  }

  #stopVariantWait() {
    if (this.#variantPoll) window.clearInterval(this.#variantPoll);
    if (this.#variantTimeout) window.clearTimeout(this.#variantTimeout);
    this.#variantPoll = undefined;
    this.#variantTimeout = undefined;
    this.#variantUpdating = false;
  }

  #guardAddToCart = (event) => {
    if (!this.#variantUpdating) return;
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[ref="addToCartButton"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  #guardSubmit = (event) => {
    if (!this.#variantUpdating) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  #handleInput = () => {
    this.#updateCounter();
    this.#validate();
  };

  #updateCounter() {
    if (!this.#input || !this.#counter) return;
    this.#counter.textContent = `${this.#input.value.length} / 10`;
  }

  #validate = () => {
    if (!this.#input || this.#input.disabled || this.#input.value === '') {
      this.#input?.setCustomValidity('');
      return;
    }

    const validCharacters = /^[\p{L} '’\-]{1,10}$/u;
    this.#input.setCustomValidity(
      validCharacters.test(this.#input.value)
        ? ''
        : 'Use letters, spaces, hyphens, and apostrophes only.'
    );
  };
}

if (!customElements.get('pawsch-pet-name')) {
  customElements.define('pawsch-pet-name', PawschPetName);
}
